import React, { useState, useEffect, useCallback } from "react";
import {
  ImageIcon,
  DollarSign,
  Users,
  ShoppingBag,
  CheckCircle2,
  XCircle,
  Star,
  Loader2,
  Wallet,
  RefreshCw,
  Search,
  Eye,
  Send,
  Check,
  X,
  ArrowRight,
  AlertCircle,
  CreditCard,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import api, { assetImage, formatIDR, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

export default function AdminConsole() {
  const { t, localizedTitle } = useI18n();
  const [activeTab, setActiveTab] = useState("withdrawals"); // "moderation" | "withdrawals"

  // Common stats & balance
  const [stats, setStats] = useState(null);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Asset moderation state
  const [assets, setAssets] = useState([]);
  const [assetFilter, setAssetFilter] = useState("pending");
  const [assetLoading, setAssetLoading] = useState(true);

  // Withdrawals state
  const [withdrawals, setWithdrawals] = useState([]);
  const [wStats, setWStats] = useState(null);
  const [wFilter, setWFilter] = useState("all");
  const [wSearch, setWSearch] = useState("");
  const [wLoading, setWLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [traceItem, setTraceItem] = useState(null);

  const fetchBalance = async () => {
    setBalanceLoading(true);
    try {
      const { data } = await api.get("/admin/tarapay/balance");
      setBalance(data);
    } catch (err) {
      setBalance({ ok: false, error: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setBalanceLoading(false);
    }
  };

  const balanceAmount = (() => {
    const d = balance?.data?.data || balance?.data;
    if (!d || typeof d !== "object") return null;
    const v = d.availableBalance ?? d.available ?? d.balance ?? d.amount;
    return v != null ? v : null;
  })();

  // Load common stats
  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  // Load assets
  const loadAssets = useCallback(() => {
    setAssetLoading(true);
    api
      .get("/admin/assets", { params: { status: assetFilter } })
      .then((r) => {
        setAssets(r.data.assets || []);
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      })
      .finally(() => {
        setAssetLoading(false);
      });
  }, [assetFilter]);

  // Load withdrawals
  const loadWithdrawals = useCallback(() => {
    setWLoading(true);
    const params = {};
    if (wFilter && wFilter !== "all") params.status = wFilter;
    if (wSearch.trim()) params.q = wSearch.trim();

    api
      .get("/admin/withdrawals", { params })
      .then((r) => {
        setWithdrawals(r.data.withdrawals || []);
        if (r.data.stats) setWStats(r.data.stats);
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      })
      .finally(() => {
        setWLoading(false);
      });
  }, [wFilter, wSearch]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (activeTab === "moderation") {
      loadAssets();
    } else {
      loadWithdrawals();
    }
  }, [activeTab, loadAssets, loadWithdrawals]);

  // Moderation action
  const moderate = async (id, action, extra = {}) => {
    try {
      let reason = null;
      if (action === "reject") {
        reason = window.prompt(t("admin.rejectReason"), "Does not meet quality guidelines");
        if (reason === null) return;
      }
      await api.post(`/admin/assets/${id}/moderate`, { action, reason, ...extra });
      toast.success(
        action === "approve"
          ? t("admin.approveSuccess")
          : action === "reject"
          ? t("admin.rejectSuccess")
          : "Updated"
      );
      loadAssets();
      loadStats();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // Withdrawal actions
  const handleDisburse = async (w) => {
    if (
      !window.confirm(
        `Lakukan disburse payout sebesar ${formatIDR(w.amount)} ke ${w.bank_code} (${w.account_no} a.n. ${w.account_name}) via Tarapay?`
      )
    ) {
      return;
    }
    setActionLoadingId(w.id);
    try {
      const res = await api.post(`/admin/withdrawals/${w.id}/disburse`);
      toast.success(res.data?.message || t("admin.disburseSuccess"));
      loadWithdrawals();
      loadStats();
      fetchBalance();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      loadWithdrawals();
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleApproveWithdrawal = async (w) => {
    setActionLoadingId(w.id);
    try {
      await api.post(`/admin/withdrawals/${w.id}/approve`);
      toast.success(t("admin.approveSuccessWithdraw"));
      loadWithdrawals();
      loadStats();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRejectWithdrawal = async (w) => {
    const reason = window.prompt(
      t("admin.confirmRejectWithdraw"),
      "Nomor rekening atau nama pemilik tidak sesuai"
    );
    if (reason === null) return;

    setActionLoadingId(w.id);
    try {
      await api.post(`/admin/withdrawals/${w.id}/reject`, { reason });
      toast.success(t("admin.rejectSuccessWithdraw"));
      loadWithdrawals();
      loadStats();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleManualComplete = async (w) => {
    const note = window.prompt(
      "Konfirmasi: Tandai penarikan ini sebagai BERHASIL (Selesai Manual). Masukkan catatan referensi transfer:",
      "Transfer manual via BCA / Mobile Banking"
    );
    if (note === null) return;

    setActionLoadingId(w.id);
    try {
      await api.post(`/admin/withdrawals/${w.id}/complete`, { note });
      toast.success(t("admin.manualSuccessWithdraw"));
      loadWithdrawals();
      loadStats();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionLoadingId(null);
    }
  };

  const assetFilters = [
    { v: "pending", l: t("admin.pending") },
    { v: "approved", l: t("admin.approved") },
    { v: "rejected", l: t("admin.rejected") },
    { v: "all", l: t("admin.all") },
  ];

  const withdrawalFilters = [
    { v: "all", l: t("admin.allWithdrawals") },
    { v: "pending_all", l: t("admin.pendingWithdrawals") },
    { v: "pending", l: t("admin.pendingFilter") },
    { v: "approved", l: t("admin.approvedFilter") },
    { v: "processing", l: t("admin.processingFilter") },
    { v: "success", l: t("admin.successFilter") },
    { v: "rejected", l: t("admin.rejectedFilter") },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
            {t("admin.title")}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{t("admin.subtitle")}</p>
        </div>

        {/* Global Tab Switcher */}
        <div className="inline-flex rounded-xl bg-slate-100 p-1 border border-slate-200">
          <button
            onClick={() => setActiveTab("withdrawals")}
            data-testid="tab-withdrawals"
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-xs sm:text-sm font-semibold transition ${
              activeTab === "withdrawals"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Wallet size={16} />
            <span>{t("admin.tabWithdrawals")}</span>
            {wStats && (wStats.pending + wStats.processing + wStats.approved) > 0 && (
              <span className="rounded-full bg-amber-500 px-1.5 py-0.2 text-[10px] font-bold text-white">
                {wStats.pending + wStats.processing + wStats.approved}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("moderation")}
            data-testid="tab-moderation"
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-xs sm:text-sm font-semibold transition ${
              activeTab === "moderation"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <ImageIcon size={16} />
            <span>{t("admin.tabAssets")}</span>
            {stats?.pending > 0 && (
              <span className="rounded-full bg-amber-500 px-1.5 py-0.2 text-[10px] font-bold text-white">
                {stats.pending}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Top Stats Overview */}
      {stats && (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {[
            { icon: ImageIcon, label: t("admin.totalAssets"), value: stats.total_assets, color: "text-fuchsia-600" },
            { icon: DollarSign, label: t("admin.revenue"), value: formatIDR(stats.revenue), color: "text-emerald-600" },
            { icon: Users, label: t("admin.users"), value: stats.users, color: "text-blue-600" },
            { icon: ShoppingBag, label: t("admin.orders"), value: stats.orders, color: "text-amber-600" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                <Icon size={20} className={s.color} />
                <p className="mt-2 font-display text-xl sm:text-2xl font-extrabold text-slate-900">{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* ============================================================== */}
      {/* TAB 1: PENGANJUAN & RIWAYAT PENARIKAN (WITHDRAWALS / SETTLEMENT) */}
      {/* ============================================================== */}
      {activeTab === "withdrawals" && (
        <div className="mt-8 space-y-6">
          {/* Subheader & Withdrawal Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase">{t("admin.totalWithdrawals")}</p>
                  <p className="mt-1 font-display text-2xl font-extrabold text-slate-900">
                    {wStats?.total ?? "—"}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700">
                  <CreditCard size={20} />
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Total permohonan pencairan dana kontributor
              </p>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-amber-800 uppercase">{t("admin.pendingWithdrawals")}</p>
                  <p className="mt-1 font-display text-2xl font-extrabold text-amber-900">
                    {wStats ? (wStats.pending + wStats.processing + wStats.approved) : "—"}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-amber-200/70 flex items-center justify-center text-amber-800">
                  <Clock size={20} />
                </div>
              </div>
              <p className="mt-2 text-xs text-amber-800 font-medium">
                Nominal tertahan: {formatIDR(wStats?.pending_amount ?? 0)}
              </p>
            </div>

            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-emerald-800 uppercase">{t("admin.successWithdrawals")}</p>
                  <p className="mt-1 font-display text-2xl font-extrabold text-emerald-900">
                    {formatIDR(wStats?.total_disbursed ?? 0)}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-emerald-200/70 flex items-center justify-center text-emerald-800">
                  <CheckCircle2 size={20} />
                </div>
              </div>
              <p className="mt-2 text-xs text-emerald-800 font-medium">
                {wStats?.success ?? 0} transaksi berhasil ditransfer
              </p>
            </div>
          </div>

          {/* Tarapay Merchant Balance Card */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900">
                  <Wallet size={18} className="text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase">{t("admin.merchantBalance")}</p>
                  {balance ? (
                    balanceAmount != null ? (
                      <p className="font-mono text-xl font-extrabold text-slate-900" data-testid="merchant-balance-value">
                        {formatIDR(balanceAmount)}
                      </p>
                    ) : (
                      <p className="text-xs text-amber-600 font-medium" data-testid="merchant-balance-unavailable">
                        {balance.error || t("admin.balanceUnavailable")}
                      </p>
                    )
                  ) : (
                    <p className="text-sm font-semibold text-slate-400">Klik cek saldo untuk sinkronisasi Tarapay</p>
                  )}
                </div>
              </div>
              <button
                onClick={fetchBalance}
                disabled={balanceLoading}
                className="flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 transition disabled:opacity-60"
                data-testid="check-balance-btn"
              >
                {balanceLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                {t("admin.checkBalance")}
              </button>
            </div>
          </div>

          {/* Filter Bar & Search */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pt-2">
            <div className="flex flex-wrap items-center gap-1.5 overflow-x-auto pb-1">
              {withdrawalFilters.map((f) => (
                <button
                  key={f.v}
                  onClick={() => setWFilter(f.v)}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition whitespace-nowrap ${
                    wFilter === f.v
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`withdraw-filter-${f.v}`}
                >
                  {f.l}
                </button>
              ))}
            </div>

            <div className="relative min-w-[260px]">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder={t("admin.searchWithdrawals")}
                value={wSearch}
                onChange={(e) => setWSearch(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-8 py-2 text-xs text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
              />
              {wSearch && (
                <button
                  onClick={() => setWSearch("")}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Withdrawals Table */}
          {wLoading ? (
            <div className="flex min-h-[30vh] items-center justify-center">
              <Loader2 size={28} className="animate-spin text-slate-400" />
            </div>
          ) : withdrawals.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center">
              <Wallet className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-700">{t("admin.noWithdrawals")}</p>
              <p className="text-xs text-slate-400 mt-1">Belum ada pengajuan penarikan dana dengan filter ini.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-slate-100 bg-slate-50/70 font-semibold uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-3.5">Kode & Tanggal</th>
                      <th className="px-4 py-3.5">Kontributor</th>
                      <th className="px-4 py-3.5">Rekening Tujuan</th>
                      <th className="px-4 py-3.5 text-right">Nominal</th>
                      <th className="px-4 py-3.5 text-center">Status</th>
                      <th className="px-4 py-3.5 text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {withdrawals.map((w) => {
                      const isActionBusy = actionLoadingId === w.id;
                      return (
                        <tr key={w.id} className="hover:bg-slate-50/80 transition" data-testid={`withdraw-row-${w.id}`}>
                          {/* Kode & Tanggal */}
                          <td className="px-4 py-3.5 whitespace-nowrap">
                            <p className="font-mono text-xs font-bold text-slate-900">{w.mch_order_no || w.id.slice(0, 8)}</p>
                            <p className="text-[11px] text-slate-400 mt-0.5">
                              {w.created_at ? new Date(w.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                            </p>
                          </td>

                          {/* Kontributor */}
                          <td className="px-4 py-3.5">
                            <p className="font-semibold text-slate-900">{w.user_name}</p>
                            <p className="text-[11px] text-slate-500">{w.user_email}</p>
                          </td>

                          {/* Rekening Tujuan */}
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-slate-900">{w.bank_code}</span>
                              {w.bank_id && (
                                <span className="rounded bg-slate-100 px-1.5 py-0.2 text-[10px] font-mono text-slate-600">
                                  ID: {w.bank_id}
                                </span>
                              )}
                            </div>
                            <p className="font-mono text-xs text-slate-700 mt-0.5">
                              {w.account_no} <span className="font-sans text-slate-400">·</span> a.n. {w.account_name}
                            </p>
                          </td>

                          {/* Nominal */}
                          <td className="px-4 py-3.5 text-right whitespace-nowrap">
                            <p className="font-mono text-sm font-extrabold text-slate-900">{formatIDR(w.amount)}</p>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3.5 text-center whitespace-nowrap">
                            <WithdrawalStatusBadge status={w.status} t={t} />
                          </td>

                          {/* Aksi */}
                          <td className="px-4 py-3.5 text-right whitespace-nowrap">
                            <div className="inline-flex items-center gap-1.5">
                              {/* Trace Button */}
                              <button
                                onClick={() => setTraceItem(w)}
                                title={t("admin.trace")}
                                className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 transition"
                                data-testid={`trace-btn-${w.id}`}
                              >
                                <Eye size={13} />
                                <span>{t("admin.trace")}</span>
                              </button>

                              {/* Disburse Button (Tarapay payout) */}
                              {["pending", "approved", "processing", "failed"].includes(w.status) && (
                                <button
                                  onClick={() => handleDisburse(w)}
                                  disabled={isActionBusy}
                                  title="Kirim Payout via Tarapay"
                                  className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition disabled:opacity-50"
                                  data-testid={`disburse-btn-${w.id}`}
                                >
                                  {isActionBusy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                                  <span>{t("admin.disburse")}</span>
                                </button>
                              )}

                              {/* Approve Button */}
                              {w.status === "pending" && (
                                <button
                                  onClick={() => handleApproveWithdrawal(w)}
                                  disabled={isActionBusy}
                                  title={t("admin.approveWithdraw")}
                                  className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition disabled:opacity-50"
                                  data-testid={`approve-wd-btn-${w.id}`}
                                >
                                  <Check size={13} />
                                  <span>{t("admin.approveWithdraw")}</span>
                                </button>
                              )}

                              {/* Reject Button */}
                              {["pending", "approved", "processing"].includes(w.status) && (
                                <button
                                  onClick={() => handleRejectWithdrawal(w)}
                                  disabled={isActionBusy}
                                  title={t("admin.rejectWithdraw")}
                                  className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-2 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 transition disabled:opacity-50"
                                  data-testid={`reject-wd-btn-${w.id}`}
                                >
                                  <X size={13} />
                                  <span>{t("admin.rejectWithdraw")}</span>
                                </button>
                              )}

                              {/* Manual Success Button */}
                              {["pending", "approved", "processing"].includes(w.status) && (
                                <button
                                  onClick={() => handleManualComplete(w)}
                                  disabled={isActionBusy}
                                  title={t("admin.manualSuccess")}
                                  className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200 transition disabled:opacity-50"
                                  data-testid={`complete-wd-btn-${w.id}`}
                                >
                                  <CheckCircle2 size={13} />
                                  <span>Selesai</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================================== */}
      {/* TAB 2: MODERASI ASET (ASSETS MODERATION QUEUE)                 */}
      {/* ============================================================== */}
      {activeTab === "moderation" && (
        <div className="mt-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-slate-900">{t("admin.queue")}</h2>
            <div className="flex gap-1.5">
              {assetFilters.map((f) => (
                <button
                  key={f.v}
                  onClick={() => setAssetFilter(f.v)}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                    assetFilter === f.v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`admin-filter-${f.v}`}
                >
                  {f.l}
                  {f.v === "pending" && stats?.pending > 0 && (
                    <span className="ml-1.5 rounded-full bg-amber-400 px-1.5 text-[10px] text-slate-900">
                      {stats.pending}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {assetLoading ? (
            <div className="flex min-h-[30vh] items-center justify-center">
              <Loader2 size={26} className="animate-spin text-slate-400" />
            </div>
          ) : assets.length === 0 ? (
            <p className="py-16 text-center text-sm text-slate-500">{t("common.noResults")}</p>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              {assets.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-4 border-b border-slate-100 p-4 last:border-0"
                  data-testid={`admin-asset-${a.id}`}
                >
                  <img src={assetImage(a)} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="line-clamp-1 font-semibold text-slate-900">{localizedTitle(a)}</h3>
                      {a.featured && <Star size={14} className="fill-amber-400 text-amber-400" />}
                    </div>
                    <p className="text-xs text-slate-500">
                      {a.contributor_name} · {a.category} · {formatIDR(a.price)}
                    </p>
                    <span
                      className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        a.status === "approved"
                          ? "bg-emerald-100 text-emerald-700"
                          : a.status === "rejected"
                          ? "bg-red-100 text-red-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {a.status}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {a.status !== "approved" && (
                      <button
                        onClick={() => moderate(a.id, "approve")}
                        className="flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-600 transition"
                        data-testid={`approve-btn-${a.id}`}
                      >
                        <CheckCircle2 size={14} /> {t("admin.approve")}
                      </button>
                    )}
                    {a.status !== "rejected" && (
                      <button
                        onClick={() => moderate(a.id, "reject")}
                        className="flex items-center gap-1 rounded-lg bg-red-500 px-3 py-2 text-xs font-semibold text-white hover:bg-red-600 transition"
                        data-testid={`reject-btn-${a.id}`}
                      >
                        <XCircle size={14} /> {t("admin.reject")}
                      </button>
                    )}
                    {a.status === "approved" && (
                      <button
                        onClick={() => moderate(a.id, "feature", { featured: !a.featured })}
                        className={`flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-semibold transition ${
                          a.featured ? "bg-slate-200 text-slate-700" : "bg-amber-400 text-slate-900 hover:bg-amber-500"
                        }`}
                        data-testid={`feature-btn-${a.id}`}
                      >
                        <Star size={14} /> {a.featured ? t("admin.unfeature") : t("admin.feature")}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Trace Modal */}
      {traceItem && (
        <WithdrawalTraceModal
          item={traceItem}
          onClose={() => setTraceItem(null)}
          t={t}
          onDisburse={handleDisburse}
          onReject={handleRejectWithdrawal}
          onComplete={handleManualComplete}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status Badge Helper
// ---------------------------------------------------------------------------
function WithdrawalStatusBadge({ status, t }) {
  const s = String(status || "").toLowerCase();
  if (s === "success" || s === "completed") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-800">
        <CheckCircle2 size={12} />
        {t("withdrawStatus.success") || "Berhasil"}
      </span>
    );
  }
  if (s === "approved") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-bold text-blue-800">
        <Check size={12} />
        {t("withdrawStatus.approved") || "Disetujui"}
      </span>
    );
  }
  if (s === "processing") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2.5 py-1 text-[11px] font-bold text-purple-800">
        <Loader2 size={12} className="animate-spin" />
        {t("withdrawStatus.processing") || "Diproses Tarapay"}
      </span>
    );
  }
  if (s === "rejected") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-bold text-red-800">
        <XCircle size={12} />
        {t("withdrawStatus.rejected") || "Ditolak (Saldo Kembali)"}
      </span>
    );
  }
  if (s === "failed") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-bold text-rose-800">
        <AlertCircle size={12} />
        {t("withdrawStatus.failed") || "Gagal"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-800">
      <Clock size={12} />
      {t("withdrawStatus.pending") || "Menunggu Persetujuan"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Trace Modal (Traceability like in Waruna)
// ---------------------------------------------------------------------------
function WithdrawalTraceModal({ item, onClose, t, onDisburse, onReject, onComplete }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/admin/withdrawals/${item.id}/trace`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [item.id]);

  const w = data?.withdrawal || item;
  const contributor = data?.contributor;
  const earnings = data?.earnings;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="trace-modal-backdrop"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-2xl my-8 overflow-hidden shadow-2xl border border-slate-100"
        onClick={(e) => e.stopPropagation()}
        data-testid="trace-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50/50">
          <div>
            <h3 className="font-display font-bold text-slate-900 text-lg">Penelusuran Pencairan & Payout</h3>
            <p className="text-xs text-slate-400 font-mono mt-0.5">{w.mch_order_no || w.id}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 size={30} className="animate-spin text-slate-400 mx-auto" />
            <p className="mt-3 text-xs text-slate-400">Memuat detail penelusuran...</p>
          </div>
        ) : (
          <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
            {/* Step Chain */}
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
              {["Pengajuan", "Review Admin", "Tarapay Gateway", "Rekening Bank"].map((step, idx) => (
                <React.Fragment key={step}>
                  <span className="rounded-lg bg-emerald-50 px-2.5 py-1 text-emerald-800 border border-emerald-100">
                    {step}
                  </span>
                  {idx < 3 && <ArrowRight size={12} className="text-slate-300" />}
                </React.Fragment>
              ))}
            </div>

            {/* Quick Stat Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-xl bg-slate-50 p-3">
                <p className="text-[10px] font-bold uppercase text-slate-400">Kontributor</p>
                <p className="mt-1 font-semibold text-slate-900 text-sm truncate">{w.user_name}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <p className="text-[10px] font-bold uppercase text-slate-400">Nominal Penarikan</p>
                <p className="mt-1 font-extrabold text-emerald-700 text-sm">{formatIDR(w.amount)}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <p className="text-[10px] font-bold uppercase text-slate-400">Bank Tujuan</p>
                <p className="mt-1 font-semibold text-slate-900 text-sm truncate">{w.bank_code}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <p className="text-[10px] font-bold uppercase text-slate-400">Status</p>
                <div className="mt-1">
                  <WithdrawalStatusBadge status={w.status} t={t} />
                </div>
              </div>
            </div>

            {/* Rekening Tujuan Box */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                Rincian Rekening Penerima
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-slate-400">Bank / E-Wallet:</span>
                  <p className="font-semibold text-slate-900 mt-0.5">
                    {w.bank_code} {w.bank_id ? `(Bank ID Tarapay: ${w.bank_id})` : ""}
                  </p>
                </div>
                <div>
                  <span className="text-slate-400">Nomor Rekening:</span>
                  <p className="font-mono font-bold text-slate-900 mt-0.5">{w.account_no}</p>
                </div>
                <div>
                  <span className="text-slate-400">Nama Pemilik Rekening:</span>
                  <p className="font-semibold text-slate-900 mt-0.5">{w.account_name}</p>
                </div>
                <div>
                  <span className="text-slate-400">Tipe Akun Tarapay:</span>
                  <p className="font-medium text-slate-900 mt-0.5">
                    {w.account_type === "2" ? "2 (E-Wallet)" : "1 (Bank Account)"}
                  </p>
                </div>
              </div>
            </div>

            {/* Contributor Balance Summary */}
            {earnings && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                  Kondisi Saldo Kontributor Saat Ini
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div>
                    <span className="text-slate-400">Total Penjualan:</span>
                    <p className="font-semibold text-slate-900 mt-0.5">{earnings.total_sales} item</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Total Pendapatan:</span>
                    <p className="font-semibold text-slate-900 mt-0.5">{formatIDR(earnings.total_earnings)}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Total Ditarik:</span>
                    <p className="font-semibold text-slate-900 mt-0.5">{formatIDR(earnings.withdrawn)}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Saldo Tersedia:</span>
                    <p className="font-extrabold text-emerald-600 mt-0.5">{formatIDR(earnings.available_balance)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Rejection / Note / Error alert */}
            {w.rejection_reason && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-xs text-red-900">
                <p className="font-bold flex items-center gap-1.5 text-red-800">
                  <XCircle size={14} /> Alasan Penolakan:
                </p>
                <p className="mt-1">{w.rejection_reason}</p>
                <p className="mt-1 text-[11px] text-red-700">Dana telah dikembalikan ke saldo kontributor.</p>
              </div>
            )}

            {w.last_error && !w.rejection_reason && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
                <p className="font-bold flex items-center gap-1.5 text-amber-800">
                  <AlertCircle size={14} /> Pesan Gateway Terakhir:
                </p>
                <p className="mt-1 font-mono text-[11px]">{w.last_error}</p>
              </div>
            )}

            {w.admin_note && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">
                <p className="font-bold text-slate-800">Catatan Admin:</p>
                <p className="mt-1">{w.admin_note}</p>
              </div>
            )}

            {/* Gateway Tarapay Response Section */}
            <div className="rounded-2xl border border-slate-200 overflow-hidden">
              <button
                onClick={() => setShowRaw(!showRaw)}
                className="w-full flex items-center justify-between p-3.5 bg-slate-50 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition"
              >
                <span>Respon Gateway Tarapay (Raw JSON)</span>
                {showRaw ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showRaw && (
                <div className="p-3 bg-slate-900 text-slate-100 font-mono text-[11px] overflow-x-auto max-h-56">
                  <pre>{JSON.stringify(w.tarapay_response || { info: "Belum ada respon gateway" }, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal Footer Actions */}
        <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition"
          >
            Tutup
          </button>

          <div className="flex items-center gap-2">
            {["pending", "approved", "processing"].includes(w.status) && (
              <button
                onClick={() => {
                  onClose();
                  onReject(w);
                }}
                className="rounded-xl bg-red-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-red-700 transition"
              >
                Tolak Pengajuan
              </button>
            )}

            {["pending", "approved", "processing", "failed"].includes(w.status) && (
              <button
                onClick={() => {
                  onClose();
                  onDisburse(w);
                }}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700 transition flex items-center gap-1.5"
              >
                <Send size={13} />
                <span>Disburse Sekarang</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
